import { ArrowDown, ArrowUp, Info, Minus } from 'lucide-react';
import { Link } from 'react-router';
import type { Metric } from '#shared/types';
import { cn } from '#client/lib/cn';
import { formatDelta, formatMetricValue } from './format';

/**
 * One headline number.
 *
 * A sibling of `StatTile` rather than a reuse of it: that one takes a
 * server-built `KpiTile` with a sparkline and a fixed tone vocabulary, and this
 * one carries a comparison delta and a caveat instead. Forcing them together
 * would mean a props type where half the fields are unused on any given call.
 *
 * The two rules it does inherit, because they are the reason `StatTile` is
 * shaped the way it is:
 *
 *   * **never render a bare zero** — the server supplies a `zero_label`, and
 *     "Nothing overdue" is a different message from "0";
 *   * **every tile drills through** — a number nobody can click is a number
 *     nobody can investigate.
 */
function MetricTile({ metric }: { metric: Metric }) {
  const body = (
    <>
      <p className="c-an-tile__label">
        {metric.label}
        {metric.caveat && (
          <span className="c-an-tile__info" title={metric.caveat}>
            <Info size={12} aria-hidden="true" />
            <span className="sr-only">{metric.caveat}</span>
          </span>
        )}
      </p>

      <p className={cn('c-an-tile__value', 'tnum', !metric.known && 'is-unknown')}>
        {formatMetricValue(metric)}
      </p>

      {metric.delta ? <Delta metric={metric} /> : <span className="c-an-tile__spacer" />}
    </>
  );

  if (metric.href === null) return <div className="c-an-tile">{body}</div>;

  return (
    <Link className="c-an-tile c-an-tile--link" to={metric.href}>
      {body}
    </Link>
  );
}

function Delta({ metric }: { metric: Metric }) {
  const delta = metric.delta;
  if (!delta) return null;

  const text = formatDelta(delta.change, delta.change_ratio, metric.unit);
  if (text === null) {
    // A rise from zero has no percentage, and inventing one would be the
    // "up 100%" headline that means nothing. Say what actually happened.
    return <span className="c-an-tile__delta">new this period</span>;
  }

  const rising = (delta.change ?? 0) > 0;
  const flat = (delta.change ?? 0) === 0;

  // Which way is good comes from the server, because the tile cannot know.
  // Colouring a rise green because the arrow points up congratulates a lab on
  // its growing backlog.
  const good =
    delta.direction === 'neutral' || flat
      ? null
      : delta.direction === 'up_is_good'
        ? rising
        : !rising;

  const Icon = flat ? Minus : rising ? ArrowUp : ArrowDown;

  return (
    <span
      className={cn('c-an-tile__delta', good === true && 'is-good', good === false && 'is-bad')}
    >
      {/* The arrow repeats the sign, which the text already carries — so it is
          decoration and the label below is the accessible version. */}
      <Icon size={12} aria-hidden="true" />
      {text}
      <span className="sr-only">
        {` compared with the ${delta.basis === 'year' ? 'same period last year' : 'previous period'}`}
      </span>
    </span>
  );
}

/** The row of tiles at the top of every section. */
export function MetricRow({ metrics }: { metrics: readonly Metric[] }) {
  if (metrics.length === 0) return null;

  return (
    <div className="c-an-tiles">
      {metrics.map((metric) => (
        <MetricTile key={metric.key} metric={metric} />
      ))}
    </div>
  );
}
